import useOutsideClick from "@/src/useOutsideClick";
import { Portal } from "@headlessui/react";
import { useRef, useState } from "react";
import { HiOutlineChevronDown } from "react-icons/hi";
import { useTransition } from "transition-hook";

export const ModeDropdown: React.FunctionComponent<{
  isAgentMode: boolean;
  onChange: (isAgentMode: boolean) => void;
  collapsed: boolean;
}> = ({ isAgentMode, onChange, collapsed }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { shouldMount, stage } = useTransition(isOpen, 150);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const outsideClickRef = useOutsideClick<HTMLDivElement>(() =>
    setIsOpen(false)
  );

  if (collapsed) return null;

  const stageIsEnter = stage === "enter";
  const currentLabel = isAgentMode ? "for Agents" : "for Apps";

  let menuPosition = { top: 0, left: 0 };
  if (shouldMount && buttonRef.current) {
    const rect = buttonRef.current.getBoundingClientRect();
    menuPosition = {
      top: rect.bottom + 4,
      left: rect.left,
    };
  }

  return (
    <div className="relative" ref={outsideClickRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Switch between Oso for Apps and Oso for Agents"
        aria-expanded={isOpen}
        aria-haspopup="true"
        className="flex items-center space-x-1 text-sm text-gray-700 hover:text-gray-900 transition-colors"
      >
        <span className="font-space text-lg font-light">{currentLabel}</span>
        <HiOutlineChevronDown
          className={`h-4 w-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {shouldMount && (
        <Portal>
          <div
            className={`fixed py-2 rounded-lg shadow-lg z-10 w-[150px] transition-all flex flex-col bg-white border border-gray-200 ${
              stageIsEnter
                ? "opacity-100 scale-100 translate-x-0 translate-y-0"
                : "opacity-0 scale-95 translate-y-2"
            }`}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
          >
            <button
              role="menuitem"
              onClick={() => {
                onChange(false);
                setIsOpen(false);
              }}
              className={`transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100 px-4 py-2 text-left ${
                !isAgentMode ? "font-semibold bg-gray-50" : ""
              }`}
            >
              Apps
            </button>
            <button
              role="menuitem"
              onClick={() => {
                onChange(true);
                setIsOpen(false);
              }}
              className={`transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-100 px-4 py-2 text-left ${
                isAgentMode ? "font-semibold bg-gray-50" : ""
              }`}
            >
              Agents
            </button>
          </div>
        </Portal>
      )}
    </div>
  );
};
